import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import Navbar from "../../Navbar";
import { logEventos } from "../../../services/services";
import { formatCuit, formatMoneda } from "../pago_proveedores/utils";
import { getMenuesUsuario, getRechazoById, getRechazos, getChequesResumen } from "./ApiServices";
import {
  MENU_CHEQUES,
  normalizarRutaMenu,
} from "./cheques.constants";
import ExpedienteArchivos from "./ExpedienteArchivos";
import ModalFinanzasNd from "./ModalFinanzasNd";
import ModalPagosRechazo from "./ModalPagosRechazo";
import ModalJefeRechazo from "./ModalJefeRechazo";
import ModalCcRechazo from "./ModalCcRechazo";
import {
  BANDEJAS_RECHAZO,
  ESTADOS_RECHAZO,
  ESTADOS_RECHAZO_FILTRO,
  MENU_CHEQUES_RECHAZOS,
  ORIGEN_RECHAZO,
  bandejaRechazoDesdePath,
  badgesRechazosDesdeResumen,
  colorEstadoRechazo,
  esReposicionExenta,
  labelEstadoRechazo,
  labelOrigenRechazo,
  textoMotivoRechazo,
  codigoMotivoRechazo,
  tituloBandejaRechazo,
} from "./chequesRechazos.constants";
import { VISTA_ECHEQUE, rutaEcheq } from "./echeqMenu.constants";
import TabsCircuitoEcheq, {
  labelTabConBadge,
  sxTabsConBadge,
} from "./TabsCircuitoEcheq";

const PAGE_SIZE = 50;

const formatearFecha = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : "—";
};

const formatearFechaHora = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "—";
};

const filtrosIniciales = () => ({
  estado: "",
  origen: "",
  q: "",
});

const CampoDetalle = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value ?? "—"}</Typography>
  </Grid>
);

const idRechazoDe = (row) => row?.id ?? row?.id_rechazo ?? null;

const flagAccion = (row, key) => {
  const v = row?.acciones?.[key];
  return typeof v === "boolean" ? v : null;
};

const puedeFinanzasNd = (row, menus, bandeja) => {
  if (!menus.finanzas || bandeja !== BANDEJAS_RECHAZO.FINANZAS) return false;
  const flag = flagAccion(row, "puede_finanzas_nd");
  if (flag !== null) return flag;
  return row?.estado === ESTADOS_RECHAZO.PENDIENTE_FINANZAS_ND;
};

const puedePagos = (row, menus, bandeja) => {
  if (!menus.pagos || bandeja !== BANDEJAS_RECHAZO.PAGOS) return false;
  const flag = flagAccion(row, "puede_pagos");
  if (flag !== null) return flag;
  return row?.estado === ESTADOS_RECHAZO.PENDIENTE_PAGOS;
};

const puedeJefe = (row, menus, bandeja) => {
  if (!menus.jefe || bandeja !== BANDEJAS_RECHAZO.JEFE) return false;
  const flag = flagAccion(row, "puede_jefe");
  if (flag !== null) return flag;
  return row?.estado === ESTADOS_RECHAZO.PENDIENTE_JEFE;
};

const puedeCc = (row, menus, bandeja) => {
  if (!menus.cc || bandeja !== BANDEJAS_RECHAZO.CC) return false;
  const flag = flagAccion(row, "puede_cc");
  if (flag !== null) return flag;
  return row?.estado === ESTADOS_RECHAZO.PENDIENTE_CC;
};

const nroChequeDe = (row) =>
  row?.nro_cheque || row?.cheque?.nro_cheque || row?.cheque_nro || "—";

const importeDe = (row) =>
  row?.importe_cheque ?? row?.importe ?? row?.cheque?.importe;

export default function ListadoChequesRechazos() {
  const location = useLocation();
  const navigate = useNavigate();
  const bandejaPath = bandejaRechazoDesdePath(location.pathname, location.search);

  const [bandeja, setBandeja] = useState(bandejaPath || BANDEJAS_RECHAZO.FINANZAS);
  const [menus, setMenus] = useState({
    finanzas: false,
    pagos: false,
    jefe: false,
    cc: false,
  });
  const [menusCheques, setMenusCheques] = useState({
    cc: false,
    finanzas: false,
    cartera: false,
  });
  const [cargandoMenus, setCargandoMenus] = useState(true);
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosIniciales);
  const [page, setPage] = useState(0);
  const [rechazos, setRechazos] = useState([]);
  const [paginacion, setPaginacion] = useState({
    total: 0,
    limit: PAGE_SIZE,
    offset: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState("");
  const [modalFinanzas, setModalFinanzas] = useState({ open: false, rechazo: null });
  const [modalPagos, setModalPagos] = useState({ open: false, rechazo: null });
  const [modalJefe, setModalJefe] = useState({ open: false, rechazo: null });
  const [modalCc, setModalCc] = useState({ open: false, rechazo: null });
  const [badges, setBadges] = useState({
    rechazos_finanzas: 0,
    rechazos_pagos: 0,
    rechazos_jefe: 0,
    rechazos_cc: 0,
  });
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  const cargarResumen = useCallback(async () => {
    try {
      const data = await getChequesResumen();
      setBadges(badgesRechazosDesdeResumen(data?.badges || {}));
    } catch {
      // badges opcionales
    }
  }, []);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      setCargandoMenus(true);
      try {
        const lista = await getMenuesUsuario();
        if (cancelado) return;
        const rutas = new Set(
          (Array.isArray(lista) ? lista : []).map((m) =>
            normalizarRutaMenu(m?.ruta)
          )
        );
        setMenus({
          finanzas: rutas.has(MENU_CHEQUES_RECHAZOS.FINANZAS),
          pagos: rutas.has(MENU_CHEQUES_RECHAZOS.PAGOS),
          jefe: rutas.has(MENU_CHEQUES_RECHAZOS.JEFE),
          cc: rutas.has(MENU_CHEQUES_RECHAZOS.CC),
        });
        setMenusCheques({
          cc: rutas.has(MENU_CHEQUES.CC),
          finanzas: rutas.has(MENU_CHEQUES.FINANZAS),
          cartera: rutas.has(MENU_CHEQUES.CARTERA),
        });
      } catch {
        if (!cancelado) {
          setMenus({ finanzas: false, pagos: false, jefe: false, cc: false });
          setMenusCheques({ cc: false, finanzas: false, cartera: false });
        }
      } finally {
        if (!cancelado) setCargandoMenus(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    cargarResumen();
  }, [cargarResumen]);

  const tabsDisponibles = useMemo(() => {
    const tabs = [];
    if (menus.finanzas) tabs.push(BANDEJAS_RECHAZO.FINANZAS);
    if (menus.pagos) tabs.push(BANDEJAS_RECHAZO.PAGOS);
    if (menus.jefe) tabs.push(BANDEJAS_RECHAZO.JEFE);
    if (menus.cc) tabs.push(BANDEJAS_RECHAZO.CC);
    return tabs;
  }, [menus]);

  useEffect(() => {
    if (cargandoMenus || !tabsDisponibles.length) return;
    const desdeUrl =
      bandejaPath && tabsDisponibles.includes(bandejaPath) ? bandejaPath : null;
    const siguiente = desdeUrl || (tabsDisponibles.includes(bandeja) ? bandeja : tabsDisponibles[0]);
    if (siguiente !== bandeja) {
      setBandeja(siguiente);
      setPage(0);
      return;
    }
    const destino = rutaEcheq({ vista: VISTA_ECHEQUE.RECHAZOS, bandeja: siguiente });
    const actual = `${location.pathname}${location.search}`;
    if (location.pathname.endsWith("/echeq") && actual !== destino) {
      navigate(destino, { replace: true });
    }
  }, [
    cargandoMenus,
    tabsDisponibles,
    bandeja,
    bandejaPath,
    location.pathname,
    location.search,
    navigate,
  ]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRechazos({
        ...filtrosAplicados,
        bandeja,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      const lista = Array.isArray(data.rechazos)
        ? data.rechazos
        : Array.isArray(data.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];
      setRechazos(lista);
      setPaginacion({
        total: Number(data.total ?? lista.length),
        limit: Number(data.limit ?? PAGE_SIZE),
        offset: Number(data.offset ?? page * PAGE_SIZE),
      });
    } catch (err) {
      setError(err.message || "Error al cargar rechazos");
      setRechazos([]);
    } finally {
      setLoading(false);
    }
  }, [filtrosAplicados, page, bandeja]);

  useEffect(() => {
    if (cargandoMenus || !tabsDisponibles.includes(bandeja)) return;
    logEventos(
      `acceso al menú Cheques rechazados (${bandeja})`,
      "Proveedores",
      "Cheques rechazados",
      localStorage.getItem("user")
    );
  }, [bandeja, cargandoMenus, tabsDisponibles]);

  useEffect(() => {
    if (cargandoMenus || !tabsDisponibles.includes(bandeja)) return;
    cargar();
  }, [cargar, cargandoMenus, tabsDisponibles, bandeja]);

  const aplicarFiltros = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setFiltrosAplicados({ ...filtros });
  };

  const limpiarFiltros = () => {
    const vacio = filtrosIniciales();
    setFiltros(vacio);
    setFiltrosAplicados(vacio);
    setPage(0);
  };

  const cambiarBandeja = (_e, value) => {
    setBandeja(value);
    setPage(0);
    navigate(rutaEcheq({ vista: VISTA_ECHEQUE.RECHAZOS, bandeja: value }), {
      replace: true,
    });
  };

  const abrirDetalle = useCallback(async (row) => {
    const id = idRechazoDe(row);
    if (!id) return;
    setDetalle(row);
    setEventos([]);
    setErrorDetalle("");
    setLoadingDetalle(true);
    try {
      const data = await getRechazoById(id);
      const rechazo = data?.rechazo || data;
      if (rechazo) setDetalle(rechazo);
      setEventos(
        Array.isArray(data?.eventos)
          ? data.eventos
          : Array.isArray(rechazo?.eventos)
            ? rechazo.eventos
            : []
      );
    } catch (err) {
      setErrorDetalle(err.message || "No se pudo cargar el detalle");
    } finally {
      setLoadingDetalle(false);
    }
  }, []);

  const refrescarTrasAccion = () => {
    setToast({
      open: true,
      message: "Acción registrada",
      severity: "success",
    });
    cargarResumen();
    if (page === 0) cargar();
    else setPage(0);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "nro_cheque",
        header: "N° cheque",
        size: 100,
        Cell: ({ row }) => nroChequeDe(row.original),
      },
      {
        accessorKey: "origen",
        header: "Origen",
        size: 140,
        Cell: ({ cell }) => labelOrigenRechazo(cell.getValue()),
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 170,
        Cell: ({ cell }) => (
          <Chip
            label={labelEstadoRechazo(cell.getValue())}
            size="small"
            color={colorEstadoRechazo(cell.getValue())}
            variant="outlined"
          />
        ),
      },
      {
        id: "motivo",
        header: "Motivo",
        size: 180,
        Cell: ({ row }) => {
          const motivo = textoMotivoRechazo(row.original) || "—";
          return (
            <Typography
              variant="body2"
              noWrap
              title={motivo}
              sx={{ maxWidth: 180 }}
            >
              {motivo}
            </Typography>
          );
        },
      },
      {
        accessorKey: "importe_cheque",
        header: "Importe",
        size: 110,
        Cell: ({ row }) => {
          const v = importeDe(row.original);
          return v != null ? formatMoneda(v) : "—";
        },
      },
      {
        accessorKey: "fecha_pago",
        header: "Fecha pago",
        size: 100,
        Cell: ({ row }) =>
          formatearFecha(
            row.original.fecha_pago || row.original.cheque?.fecha_pago
          ),
      },
      {
        id: "reposicion",
        header: "Reposición",
        size: 120,
        Cell: ({ row }) => {
          if (esReposicionExenta(row.original)) {
            return (
              <Chip label="Exenta 30d" size="small" color="warning" variant="outlined" />
            );
          }
          if (row.original.reposicion_requerida === false) {
            return <Chip label="No" size="small" variant="outlined" />;
          }
          return <Chip label="Sí" size="small" color="info" variant="outlined" />;
        },
      },
      {
        accessorKey: "proveedor_razon_social",
        header: "Proveedor / cliente",
        size: 180,
        Cell: ({ row }) => {
          const nombre =
            row.original.proveedor_razon_social ||
            row.original.cliente_razon_social ||
            row.original.cheque?.proveedor_razon_social ||
            "—";
          return (
            <Box>
              <Typography variant="body2" noWrap title={nombre} sx={{ maxWidth: 180 }}>
                {nombre}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatCuit(
                  row.original.cuit_recibido || row.original.cheque?.cuit_recibido
                )}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: "acciones",
        header: "Acciones",
        size: 200,
        enableSorting: false,
        Cell: ({ row }) => {
          const rechazo = row.original;
          return (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              onClick={(e) => e.stopPropagation()}
            >
              {puedeFinanzasNd(rechazo, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setModalFinanzas({ open: true, rechazo })}
                >
                  ND Finanzas
                </Button>
              ) : null}
              {puedePagos(rechazo, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => setModalPagos({ open: true, rechazo })}
                >
                  Pagos
                </Button>
              ) : null}
              {puedeJefe(rechazo, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => setModalJefe({ open: true, rechazo })}
                >
                  Jefe
                </Button>
              ) : null}
              {puedeCc(rechazo, menus, bandeja) ? (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={() => setModalCc({ open: true, rechazo })}
                >
                  Cerrar CC
                </Button>
              ) : null}
            </Stack>
          );
        },
      },
    ],
    [menus, bandeja]
  );

  const mostrarTabs = tabsDisponibles.length > 0;

  const tieneMenusCheques = useMemo(
    () =>
      Boolean(menusCheques.cc || menusCheques.finanzas || menusCheques.cartera),
    [menusCheques]
  );

  const pendientesRechazo =
    (menus.finanzas ? badges.rechazos_finanzas : 0) +
    (menus.pagos ? badges.rechazos_pagos : 0) +
    (menus.jefe ? badges.rechazos_jefe : 0) +
    (menus.cc ? badges.rechazos_cc : 0);

  const chequeDetalle = detalle?.cheque || detalle;

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: 4, px: 2 }}>
        <TabsCircuitoEcheq
          vista={VISTA_ECHEQUE.RECHAZOS}
          tieneCheques={tieneMenusCheques}
          tieneRechazos={tabsDisponibles.length > 0}
          pendientesRechazo={pendientesRechazo}
        />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" fontWeight={700}>
            {tituloBandejaRechazo(bandeja)}
          </Typography>
        </Stack>

        {cargandoMenus ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : null}

        {mostrarTabs && tabsDisponibles.includes(bandeja) ? (
          <Tabs
            value={bandeja}
            onChange={cambiarBandeja}
            sx={sxTabsConBadge}
          >
            {tabsDisponibles.includes(BANDEJAS_RECHAZO.FINANZAS) ? (
              <Tab
                label={labelTabConBadge("Finanzas ND", badges.rechazos_finanzas)}
                value={BANDEJAS_RECHAZO.FINANZAS}
              />
            ) : null}
            {tabsDisponibles.includes(BANDEJAS_RECHAZO.PAGOS) ? (
              <Tab
                label={labelTabConBadge("Pagos", badges.rechazos_pagos)}
                value={BANDEJAS_RECHAZO.PAGOS}
              />
            ) : null}
            {tabsDisponibles.includes(BANDEJAS_RECHAZO.JEFE) ? (
              <Tab
                label={labelTabConBadge("Jefe", badges.rechazos_jefe)}
                value={BANDEJAS_RECHAZO.JEFE}
              />
            ) : null}
            {tabsDisponibles.includes(BANDEJAS_RECHAZO.CC) ? (
              <Tab
                label={labelTabConBadge("Cuentas Corrientes", badges.rechazos_cc)}
                value={BANDEJAS_RECHAZO.CC}
              />
            ) : null}
          </Tabs>
        ) : null}

        <Paper
          component="form"
          onSubmit={aplicarFiltros}
          elevation={1}
          sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <FilterListIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros
            </Typography>
          </Stack>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                placeholder="Nro cheque, obs..."
                value={filtros.q}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, q: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Estado"
                value={filtros.estado}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, estado: e.target.value }))
                }
              >
                {ESTADOS_RECHAZO_FILTRO.map((opt) => (
                  <MenuItem key={opt.value || "todos"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Origen"
                value={filtros.origen}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, origen: e.target.value }))
                }
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value={ORIGEN_RECHAZO.RECLAMO_PROVEEDOR}>
                  {labelOrigenRechazo(ORIGEN_RECHAZO.RECLAMO_PROVEEDOR)}
                </MenuItem>
                <MenuItem value={ORIGEN_RECHAZO.EXTRACTO_BANCARIO}>
                  {labelOrigenRechazo(ORIGEN_RECHAZO.EXTRACTO_BANCARIO)}
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" size="small">
                  Buscar
                </Button>
                <Button variant="outlined" size="small" onClick={limpiarFiltros}>
                  Limpiar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <MaterialReactTable
              columns={columns}
              data={rechazos}
              getRowId={(row) => String(idRechazoDe(row) || "")}
              localization={MRT_Localization_ES}
              enableColumnActions={false}
              enableColumnFilters={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enablePagination={false}
              initialState={{ density: "compact" }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () => abrirDetalle(row.original),
                sx: { cursor: idRechazoDe(row.original) ? "pointer" : "default" },
              })}
              muiTableHeadCellProps={{
                sx: {
                  fontSize: 14,
                  color: "white",
                  fontWeight: "normal",
                  backgroundColor: "#2471A3",
                  borderColor: "black",
                },
              }}
              muiTableBodyCellProps={{
                sx: { borderColor: "black" },
              }}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {paginacion.total > 0
                      ? `${paginacion.total} rechazo${paginacion.total === 1 ? "" : "s"}`
                      : "Sin resultados"}
                  </Typography>
                  <Button size="small" startIcon={<RefreshIcon />} onClick={cargar}>
                    Actualizar
                  </Button>
                </Stack>
              )}
              renderEmptyRowsFallback={() => (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 3, textAlign: "center" }}
                >
                  No hay rechazos en esta bandeja con los filtros seleccionados.
                </Typography>
              )}
            />

            <TablePagination
              component="div"
              count={paginacion.total}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </>
        )}
      </Container>

      <ModalFinanzasNd
        open={modalFinanzas.open}
        rechazo={modalFinanzas.rechazo}
        onClose={() => setModalFinanzas({ open: false, rechazo: null })}
        onOk={refrescarTrasAccion}
      />
      <ModalPagosRechazo
        open={modalPagos.open}
        rechazo={modalPagos.rechazo}
        onClose={() => setModalPagos({ open: false, rechazo: null })}
        onOk={refrescarTrasAccion}
      />
      <ModalJefeRechazo
        open={modalJefe.open}
        rechazo={modalJefe.rechazo}
        onClose={() => setModalJefe({ open: false, rechazo: null })}
        onOk={refrescarTrasAccion}
      />
      <ModalCcRechazo
        open={modalCc.open}
        rechazo={modalCc.rechazo}
        onClose={() => setModalCc({ open: false, rechazo: null })}
        onOk={refrescarTrasAccion}
      />

      <Dialog
        open={Boolean(detalle)}
        onClose={() => setDetalle(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalle rechazo — N° {nroChequeDe(detalle || {})}
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetalle ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Stack spacing={2}>
              {errorDetalle ? (
                <Alert severity="warning">{errorDetalle}</Alert>
              ) : null}
              {esReposicionExenta(detalle) ? (
                <Alert severity="warning">
                  Reposición exenta (30 días corridos / cheque no propio)
                  {detalle?.reposicion_exenta_motivo
                    ? `: ${detalle.reposicion_exenta_motivo}`
                    : "."}
                </Alert>
              ) : null}
              <Grid container spacing={1.5}>
                <CampoDetalle
                  label="Estado"
                  value={labelEstadoRechazo(detalle?.estado)}
                />
                <CampoDetalle
                  label="Origen"
                  value={labelOrigenRechazo(detalle?.origen)}
                />
                <CampoDetalle
                  label="Motivo de rechazo"
                  value={textoMotivoRechazo(detalle) || "—"}
                />
                {codigoMotivoRechazo(detalle) != null ? (
                  <CampoDetalle
                    label="Código motivo"
                    value={codigoMotivoRechazo(detalle)}
                  />
                ) : null}
                <CampoDetalle
                  label="Importe cheque"
                  value={
                    importeDe(detalle) != null
                      ? formatMoneda(importeDe(detalle))
                      : "—"
                  }
                />
                <CampoDetalle
                  label="Gastos banco"
                  value={
                    detalle?.gastos_banco != null
                      ? formatMoneda(detalle.gastos_banco)
                      : "—"
                  }
                />
                <CampoDetalle
                  label="Gastos gestión admin"
                  value={
                    detalle?.gastos_gestion_admin != null
                      ? formatMoneda(detalle.gastos_gestion_admin)
                      : "—"
                  }
                />
                <CampoDetalle
                  label="Emite ND proveedor"
                  value={detalle?.emite_nd_proveedor || "—"}
                />
                <CampoDetalle
                  label="Reposición requerida"
                  value={
                    esReposicionExenta(detalle)
                      ? "No (exenta)"
                      : detalle?.reposicion_requerida === false
                        ? "No"
                        : "Sí"
                  }
                />
                <CampoDetalle
                  label="Cheque propio"
                  value={
                    detalle?.es_cheque_propio === true ||
                    detalle?.es_cheque_propio === 1
                      ? "Sí"
                      : detalle?.es_cheque_propio === false ||
                          detalle?.es_cheque_propio === 0
                        ? "No"
                        : "—"
                  }
                />
                <CampoDetalle label="N° cheque" value={nroChequeDe(detalle || {})} />
                <CampoDetalle
                  label="Fecha pago"
                  value={formatearFecha(
                    detalle?.fecha_pago || chequeDetalle?.fecha_pago
                  )}
                />
                <CampoDetalle
                  label="ID proveedor"
                  value={detalle?.id_proveedor ?? chequeDetalle?.id_proveedor}
                />
                <CampoDetalle
                  label="ID cliente"
                  value={detalle?.id_cliente ?? chequeDetalle?.id_cliente}
                />
                <CampoDetalle label="Observaciones" value={detalle?.obs} />
                <CampoDetalle
                  label="Ref. ND interna"
                  value={detalle?.nro_nd_interna_ref}
                />
              </Grid>

              <ExpedienteArchivos
                idRechazo={idRechazoDe(detalle)}
                disabled={detalle?.estado === ESTADOS_RECHAZO.CERRADO}
              />

              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Historial de eventos
                </Typography>
                {eventos.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin eventos registrados.
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {eventos.map((ev, idx) => (
                      <Box
                        key={ev?.id || idx}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          px: 1.5,
                          py: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {ev?.accion || ev?.evento || "Evento"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatearFechaHora(ev?.created_at || ev?.fecha)} —{" "}
                          {ev?.user || ev?.usuario || "—"}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
